import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer, Label } from 'recharts'

type Severity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info'

export interface VulnerabilityBreakdownItem {
  name: Severity
  value: number
}

interface HomeVulnerabilitiesChartProps {
  data: {
    INFO: number
    LOW: number
    MEDIUM: number
    HIGH: number
    CRITICAL: number
  }
}

const COLORS: Record<Severity, string> = {
  Critical: '#C00000',
  High: '#FF0000',
  Medium: '#FFC000',
  Low: '#00B050',
  Info: '#00B0F0'
}

const HomeVulnerabilitiesChart = ({ data: apiData }: HomeVulnerabilitiesChartProps) => {
  // Map API data to chart format
  const data: VulnerabilityBreakdownItem[] = [
    { name: 'Critical', value: apiData.CRITICAL },
    { name: 'High', value: apiData.HIGH },
    { name: 'Medium', value: apiData.MEDIUM },
    { name: 'Low', value: apiData.LOW },
    { name: 'Info', value: apiData.INFO },
  ]

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const CustomTooltip = ({ active, payload }: { active?: boolean, payload?: any[] }) => {
    if (!active || !payload || payload.length === 0) return null
    const item = payload[0]
    const name = item?.payload?.name as Severity
    const value = item?.value as number
    const color = COLORS[name]
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
          <span className="font-medium">{name}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{value}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vulnerabilities</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Total and severity distribution
        </p>
      </div>

      <div className="h-80 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              data={data}
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={120}
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
            >
              {data.map((entry) => (
                <Cell key={`cell-${entry.name}`} fill={COLORS[entry.name]} />
              ))}
              <Label
                position="center"
                content={({ viewBox }) => {
                  // Recharts provides cx, cy in viewBox for positioning
                  if (!viewBox || typeof viewBox !== 'object' || !('cx' in viewBox) || !('cy' in viewBox)) return null
                  const { cx, cy } = viewBox as { cx: number, cy: number }
                  return (
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                      <tspan className="fill-gray-900 dark:fill-white" fontSize="20" fontWeight={600}>{total}</tspan>
                      <tspan x={cx} dy="18" className="fill-gray-500 dark:fill-gray-400" fontSize="10">Total</tspan>
                    </text>
                  )
                }}
              />
            </Pie>
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend
              verticalAlign="bottom"
              height={40}
              align="center"
              formatter={(value: string, entry: any) => {
                const count = entry?.payload?.value as number
                return (
                  <span className="text-gray-900 dark:text-gray-100">
                    {value}
                    <span className="ml-1 text-gray-500 dark:text-gray-400">({count})</span>
                  </span>
                )
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default HomeVulnerabilitiesChart


